import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Users, Wrench, ClipboardCheck, Headphones, Building2, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { company } from '@/data/company';

const WhyChooseUsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Award,
      title: `Established in ${company.foundedYear}`,
      description: 'A Jordanian establishment specialised in engineering services, industrial research, equipment supply, and IT and telecom solutions.',
    },
    {
      icon: Users,
      title: 'World-leading partners',
      description: 'Large-scope engineering projects are conducted jointly with associated world-leading engineering and consultation firms.',
    },
    {
      icon: Wrench,
      title: 'In-house engineering',
      description: 'Smaller projects are engineered in-house, with the same professional standards from planning through completion.',
    },
    {
      icon: ClipboardCheck,
      title: 'Inspection and studies',
      description: 'Inspection, industrial research, consultation, and feasibility studies in both macro and micro economics.',
    },
    {
      icon: Building2,
      title: company.grade,
      description: company.electromechanical.description,
    },
    {
      icon: Headphones,
      title: 'After-sales services',
      description: 'Our vision is to be the best solutions provider by facilitating new applications, professional services, and after-sales services.',
    },
  ];

  return (
    <section ref={ref} className="py-24 relative bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-[#398024] tracking-wider uppercase mb-3">Why choose us</h2>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How MAYAN delivers
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {company.mission}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative bg-white border border-gray-200 rounded-2xl p-8 hover:border-[#398024] hover:shadow-xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-[#398024] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="text-white" size={28} />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3 pr-8">{feature.title}</h4>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              <CheckCircle2 className="absolute top-8 right-8 text-[#398024]/20 group-hover:text-[#398024]/40 transition-colors" size={20} />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-[#398024]/5 border border-[#398024]/20 rounded-2xl p-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Ready to discuss a project?</h4>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact MAYAN Technology in Amman to talk through engineering, industrial, or IT requirements.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#398024] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#2d6b1a] hover:shadow-xl transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
